/**
 * API Service for Pilates House
 *
 * This service layer abstracts all API calls to the backend.
 * Switch between WordPress and new PHP API by changing API_BASE_URL
 */

import axios from 'axios';

// API Configuration
// Switch this to use the new PHP API:
// const API_BASE_URL = 'http://localhost:8080/api/v1'; // Development
const API_BASE_URL = 'https://api-ph.azappcraft.com/api/v1'; // Production - NEW PHP API
// const API_BASE_URL = 'https://malak.azappcraft.com/wp-json/malak/v1'; // Old WordPress API

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    // Check for admin token first, then user token
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('token');
    const token = adminToken || userToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a login request - don't redirect on login failures
      const isLoginRequest = error.config?.url?.includes('/login');

      if (!isLoginRequest) {
        // Token expired or invalid - redirect to appropriate login
        const isAdmin = localStorage.getItem('adminToken');

        if (isAdmin) {
          // Admin token expired - redirect to admin login
          localStorage.removeItem('admin');
          localStorage.removeItem('adminToken');
          window.location.href = '/admin';
        } else {
          // User token expired - redirect to user login
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Authentication API
 */
export const authAPI = {
  /**
   * User login
   * @param {string} phone - User phone number
   * @param {string} password - User password
   * @returns {Promise} User data and token
   */
  loginUser: async (phone, password) => {
    const response = await api.post('/auth/login', { phone, password });

    // Only store token if login was successful
    if (response.data.success && response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  },

  /**
   * Admin login
   * @param {string} username - Admin username
   * @param {string} password - Admin password
   * @returns {Promise} Admin data and token
   */
  loginAdmin: async (username, password) => {
    const response = await api.post('/admin/login', { username, password });

    // Only store token if login was successful
    if (response.data.success && response.data.token) {
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('admin', JSON.stringify(response.data.admin));
    }

    return response.data;
  },

  /**
   * Logout (clear local storage)
   */
  logout: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    localStorage.removeItem('adminToken');
  },
};

/**
 * User API
 */
export const userAPI = {
  /**
   * Get user by ID
   * @param {number} userId - User ID
   * @returns {Promise} User data
   */
  getUser: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  /**
   * Get all users (admin only)
   * @param {number} limit - Number of users to fetch
   * @param {number} offset - Offset for pagination
   * @returns {Promise} Array of users
   */
  getAllUsers: async (limit = 100, offset = 0) => {
    const response = await api.get('/admin/users', {
      params: { limit, offset },
    });
    return response.data;
  },

  /**
   * Create user (admin only)
   * @param {Object} userData - User data
   * @returns {Promise} Created user ID
   */
  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  /**
   * Update user (admin only)
   * @param {number} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise} Success status
   */
  updateUser: async (userId, userData) => {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  },

  /**
   * Delete user (admin only)
   * @param {number} userId - User ID
   * @returns {Promise} Success status
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
};

/**
 * Training Sessions API
 */
export const trainingAPI = {
  /**
   * Get all training sessions
   * @param {string} fromDate - Optional start date (Y-m-d format)
   * @returns {Promise} Array of training sessions
   */
  getSessions: async (fromDate = null) => {
    const params = fromDate ? { from_date: fromDate } : {};
    const response = await api.get('/training-sessions', { params });
    return response.data;
  },

  /**
   * Create training session (admin only)
   * @param {Object} sessionData - Session data
   * @returns {Promise} Success status
   */
  createSession: async (sessionData) => {
    const response = await api.post('/admin/training-sessions', sessionData);
    return response.data;
  },

  /**
   * Update training session (admin only)
   * @param {string} date - Training date (Y-m-d)
   * @param {string} time - Training time (H:i)
   * @param {Object} sessionData - Updated session data
   * @returns {Promise} Success status
   */
  updateSession: async (date, time, sessionData) => {
    const response = await api.put(`/admin/training-sessions/${date}/${time}`, sessionData);
    return response.data;
  },

  /**
   * Delete training session (admin only)
   * @param {string} date - Training date (Y-m-d)
   * @param {string} time - Training time (H:i)
   * @returns {Promise} Success status
   */
  deleteSession: async (date, time) => {
    const response = await api.delete(`/admin/training-sessions/${date}/${time}`);
    return response.data;
  },
};

/**
 * Appointments API
 */
export const appointmentAPI = {
  /**
   * Get user's appointments
   * @param {number} userId - User ID
   * @returns {Promise} Array of appointments
   */
  getUserAppointments: async (userId) => {
    const response = await api.get(`/appointments/user/${userId}`);
    return response.data;
  },

  /**
   * Get count of user's appointments
   * @param {number} userId - User ID
   * @returns {Promise} Appointment count
   */
  getUserAppointmentCount: async (userId) => {
    const response = await api.get(`/appointments/user/${userId}/count`);
    return response.data;
  },

  /**
   * Book appointment
   * @param {string} trainingDate - Training date (Y-m-d)
   * @param {string} trainingTime - Training time (H:i)
   * @returns {Promise} Success status and appointment ID
   */
  bookAppointment: async (trainingDate, trainingTime) => {
    const response = await api.post('/appointments', {
      training_date: trainingDate,
      training_time: trainingTime,
    });
    return response.data;
  },

  /**
   * Cancel appointment
   * @param {number} appointmentId - Appointment ID
   * @returns {Promise} Success status
   */
  cancelAppointment: async (appointmentId) => {
    const response = await api.delete(`/appointments/${appointmentId}`);
    return response.data;
  },

  /**
   * Get all appointments (admin only)
   * @param {string} fromDate - Optional start date
   * @param {number} limit - Number of appointments to fetch
   * @param {number} offset - Offset for pagination
   * @returns {Promise} Array of appointments
   */
  getAllAppointments: async (fromDate = null, limit = 100, offset = 0) => {
    const params = { limit, offset };
    if (fromDate) params.from_date = fromDate;

    const response = await api.get('/admin/appointments', { params });
    return response.data;
  },

  /**
   * Cleanup old appointments (admin only)
   * @param {number} daysOld - Delete appointments older than this many days
   * @returns {Promise} Number of deleted appointments
   */
  cleanupOldAppointments: async (daysOld = 30) => {
    const response = await api.post('/admin/appointments/cleanup', { days_old: daysOld });
    return response.data;
  },
};

/**
 * Notifications API
 */
export const notificationAPI = {
  /**
   * Get active notifications (public endpoint)
   * @returns {Promise} Array of active notifications
   */
  getNotifications: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },

  /**
   * Get all notifications including inactive (admin only)
   * @returns {Promise} Array of all notifications
   */
  getAllNotifications: async () => {
    const response = await api.get('/admin/notifications');
    return response.data;
  },

  /**
   * Create notification (admin only)
   * @param {string} notificationText - Notification text
   * @param {boolean} sendSms - Whether to also send SMS
   * @returns {Promise} Created notification ID
   */
  createNotification: async (notificationText, sendSms = false) => {
    const response = await api.post('/admin/notifications', {
      notification_text: notificationText,
      send_sms: sendSms,
    });
    return response.data;
  },

  /**
   * Update notification (admin only)
   * @param {number} notificationId - Notification ID
   * @param {Object} data - Updated notification data
   * @returns {Promise} Success status
   */
  updateNotification: async (notificationId, data) => {
    const response = await api.put(`/admin/notifications/${notificationId}`, data);
    return response.data;
  },

  /**
   * Delete notification (admin only)
   * @param {number} notificationId - Notification ID
   * @returns {Promise} Success status
   */
  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/admin/notifications/${notificationId}`);
    return response.data;
  },
};

/**
 * Settings API
 */
export const settingsAPI = {
  /**
   * Get public settings (no auth required)
   * @returns {Promise} Public settings object
   */
  getPublicSettings: async () => {
    const response = await api.get('/settings/public');
    return response.data;
  },

  /**
   * Get all settings (admin only)
   * @returns {Promise} Settings object
   */
  getSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  /**
   * Update settings (admin only)
   * @param {Object} settings - Settings object with key-value pairs
   * @returns {Promise} Success status
   */
  updateSettings: async (settings) => {
    const response = await api.put('/admin/settings', { settings });
    return response.data;
  },
};

/**
 * SMS API
 */
export const smsAPI = {
  /**
   * Send SMS to specific recipients (admin only)
   * @param {Array<string>} recipients - Array of phone numbers
   * @param {string} message - SMS message
   * @returns {Promise} Send result
   */
  sendSms: async (recipients, message) => {
    const response = await api.post('/admin/sms/send', {
      recipients,
      message,
    });
    return response.data;
  },

  /**
   * Send SMS to all active users (admin only)
   * @param {string} message - SMS message
   * @returns {Promise} Send result
   */
  sendToAll: async (message) => {
    const response = await api.post('/admin/sms/send-to-all', {
      message,
    });
    return response.data;
  },

  /**
   * Send training reminders to users with appointments today (admin only)
   * @returns {Promise} Send result
   */
  sendReminders: async () => {
    const response = await api.post('/admin/sms/send-reminders');
    return response.data;
  },

  /**
   * Get SMS balance (admin only)
   * @returns {Promise} Balance information
   */
  getBalance: async () => {
    const response = await api.get('/admin/sms/balance');
    return response.data;
  },
};

/**
 * Health check
 */
export const healthAPI = {
  /**
   * Check API health
   * @returns {Promise} Health status
   */
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Export configured axios instance for custom calls
export default api;
